import { clearTokens, getAccessToken, getRefreshToken, setTokens } from "./auth-tokens";
import type { AuthResponse, BillingStatus } from "./types";
import type {
  AdminActivityEvent,
  AdminAuditEntryDetail,
  AdminAuditListItem,
  AdminBillingStats,
  AdminDeliveryDetail,
  AdminDeliveryListItem,
  AdminErrorLogItem,
  AdminNeedsAttentionItem,
  AdminOverviewResponse,
  AdminRouteDetail,
  AdminRouteListItem,
  AdminSettingsResponse,
  AdminSignalListItem,
  AdminSignalTrace,
  AdminSourceDetail,
  AdminSourceListItem,
  AdminTargetDetail,
  AdminTargetListItem,
  AdminTrialItem,
  AdminUserDetail,
  AdminUserListItem,
} from "./types/admin";
import type { AdminPlan, UpdatePlanRequest } from "./types/admin";
import type { PublicPlan } from "./types/plan";
import type {
  AdminSupportFilter,
  AdminSupportListResponse,
  AdminSupportTicketDetail,
  SupportTicketCategory,
  SupportTicketDetail,
  SupportTicketListResponse,
  SupportTicketPriority,
  SupportTicketStatus,
} from "./types/support";
import type {
  CreateRouteRequest,
  CreateSourceRequest,
  CreateTargetRequest,
  RouteItem,
  SignalDeliveryRow,
  Source,
  SourceCreatedResponse,
  StepCatalogEntry,
  Target,
  TargetCreatedResponse,
  TargetTestResponse,
  TestRouteRequest,
  TestRouteResponse,
  UpdateRouteRequest,
  UpdateSourceRequest,
  UpdateTargetRequest,
} from "./types/routing";
import type { UsageCurrent, UsageHistoryRow } from "./types/usage";
import type { OnboardingStatus } from "./types/onboarding";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:5000";

class ApiError extends Error {
  constructor(
    public status: number,
    public errorCode: string,
    public params?: Record<string, unknown>,
    public serverMessage?: string,
  ) {
    super(errorCode);
    this.name = 'ApiError';
  }
}

export function parseErrorResponse(body: unknown): { errorCode: string; params?: Record<string, unknown>; serverMessage?: string } {
  if (typeof body !== 'object' || body === null) return { errorCode: 'UNKNOWN' };
  const b = body as Record<string, unknown>;
  const errorCode = typeof b.errorCode === 'string' ? b.errorCode : 'UNKNOWN';
  const params = b.params && typeof b.params === 'object' ? (b.params as Record<string, unknown>) : undefined;
  const serverMessage =
    typeof b.error === 'string' ? b.error
    : typeof b.message === 'string' ? b.message
    : undefined;
  return { errorCode, params, serverMessage };
}

/**
 * English server-side message from a thrown ApiError, if the response carried one.
 * For admin surfaces only (admin is English-only); user-facing code must keep
 * translating via errorCode.
 */
export function getApiErrorMessage(err: unknown): string | undefined {
  return err instanceof ApiError ? err.serverMessage : undefined;
}

/**
 * The `errorCode` + `params` a failed request carried, for translation through
 * the `api-errors` namespace.
 *
 * User-facing catch blocks used to do `toast.error(e.message)`, and ApiError
 * sets `message` to the error code, so the toast rendered the raw code. On a
 * plan change that meant a literal "UNKNOWN" in the corner of the screen.
 * Returns undefined for anything that is not an ApiError, so callers fall back
 * to their own generic copy rather than leaking a stack message.
 */
export function getApiErrorCode(
  err: unknown,
): { errorCode: string; params?: Record<string, unknown> } | undefined {
  return err instanceof ApiError ? { errorCode: err.errorCode, params: err.params } : undefined;
}

/**
 * Builds a URL with an optional query string. Skips null/undefined/empty values so
 * callers can pass filter objects directly without pre-filtering.
 */
function qs(path: string, params?: Record<string, unknown>): string {
  if (!params) return path;
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `${path}?${s}` : path;
}

class ApiClient {
  private async request<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = getAccessToken();

    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    let response = await fetch(`${API_URL}${path}`, { ...options, headers });

    if (response.status === 401 && token) {
      const refreshed = await this.refreshToken();
      if (refreshed) {
        const newHeaders = {
          ...headers,
          Authorization: `Bearer ${getAccessToken()}`,
        };
        response = await fetch(`${API_URL}${path}`, {
          ...options,
          headers: newHeaders,
        });
      }
    }

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const { errorCode, params, serverMessage } = parseErrorResponse(body);
      throw new ApiError(response.status, errorCode, params, serverMessage);
    }

    const text = await response.text();
    return text ? JSON.parse(text) : ({} as T);
  }

  /**
   * In-flight refresh, shared by every caller that 401s while it runs.
   *
   * The backend rotates refresh tokens: `RefreshAsync` looks the user up by the
   * token presented and then overwrites `User.RefreshToken` with a new one, and
   * there is only one such column per user. Without this guard, two requests
   * that 401 together both send the same token; the first rotates it, the
   * second is looked up against a token that no longer exists, fails, and calls
   * `clearTokens()` plus a redirect to /login. The visible symptom is being
   * logged out at random, and dashboard pages fire parallel requests
   * constantly, so an expiry landing mid-page is enough to trigger it.
   */
  private refreshInFlight: Promise<boolean> | null = null;

  private refreshToken(): Promise<boolean> {
    // Reuse the running refresh rather than starting a second one. Cleared on
    // settle so a later expiry starts a fresh attempt.
    this.refreshInFlight ??= this.performRefresh().finally(() => {
      this.refreshInFlight = null;
    });

    return this.refreshInFlight;
  }

  private async performRefresh(): Promise<boolean> {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${API_URL}/api/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        clearTokens();
        window.location.href = "/login";
        return false;
      }

      const data: AuthResponse = await response.json();
      setTokens(data.accessToken, data.refreshToken);
      return true;
    } catch {
      clearTokens();
      return false;
    }
  }

  async get<T>(path: string): Promise<T> {
    return this.request<T>(path);
  }

  async post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: "PUT",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: "PATCH",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async del<T = void>(path: string): Promise<T> {
    return this.request<T>(path, { method: "DELETE" });
  }

  readonly sources = {
    list: (): Promise<Source[]> =>
      this.get<Source[]>("/api/sources"),
    get: (id: string): Promise<Source> =>
      this.get<Source>(`/api/sources/${id}`),
    create: (req: CreateSourceRequest): Promise<SourceCreatedResponse> =>
      this.post<SourceCreatedResponse>("/api/sources", req),
    update: (id: string, req: UpdateSourceRequest): Promise<Source> =>
      this.patch<Source>(`/api/sources/${id}`, req),
    delete: (id: string): Promise<void> =>
      this.del<void>(`/api/sources/${id}`),
    rotateKey: (id: string): Promise<SourceCreatedResponse> =>
      this.post<SourceCreatedResponse>(`/api/sources/${id}/rotate-key`),
    testSignal: async (
      id: string,
      rawPayload: string
    ): Promise<{ signalId: string; deliveries: string[] }> => {
      const token = getAccessToken();
      const res = await fetch(`${API_URL}/api/sources/${id}/test-signal`, {
        method: "POST",
        headers: {
          "Content-Type": "text/plain",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: rawPayload,
      });
      const text = await res.text();
      const data = text ? JSON.parse(text) : {};
      if (!res.ok) {
        const { errorCode, params, serverMessage } = parseErrorResponse(data);
        throw new ApiError(res.status, errorCode, params, serverMessage);
      }
      return data;
    },
  };

  readonly targets = {
    list: (): Promise<Target[]> =>
      this.get<Target[]>("/api/targets"),
    get: (id: string): Promise<Target> =>
      this.get<Target>(`/api/targets/${id}`),
    create: (req: CreateTargetRequest): Promise<TargetCreatedResponse> =>
      this.post<TargetCreatedResponse>("/api/targets", req),
    update: (id: string, req: UpdateTargetRequest): Promise<Target> =>
      this.patch<Target>(`/api/targets/${id}`, req),
    delete: (id: string): Promise<void> =>
      this.del<void>(`/api/targets/${id}`),
    test: (id: string): Promise<TargetTestResponse> =>
      this.post<TargetTestResponse>(`/api/targets/${id}/test`),
  };

  readonly routes = {
    list: (): Promise<RouteItem[]> =>
      this.get<RouteItem[]>("/api/routes"),
    get: (id: string): Promise<RouteItem> =>
      this.get<RouteItem>(`/api/routes/${id}`),
    create: (req: CreateRouteRequest): Promise<RouteItem> =>
      this.post<RouteItem>("/api/routes", req),
    update: (id: string, req: UpdateRouteRequest): Promise<RouteItem> =>
      this.patch<RouteItem>(`/api/routes/${id}`, req),
    delete: (id: string): Promise<void> =>
      this.del<void>(`/api/routes/${id}`),
    test: (id: string, req: TestRouteRequest): Promise<TestRouteResponse> =>
      this.post<TestRouteResponse>(`/api/routes/${id}/test`, req),
  };

  readonly steps = {
    catalog: (): Promise<StepCatalogEntry[]> =>
      this.get<StepCatalogEntry[]>("/api/steps/catalog"),
  };

  readonly usage = {
    current: (): Promise<UsageCurrent> =>
      this.get<UsageCurrent>("/api/usage/current"),
    history: (months?: number): Promise<UsageHistoryRow[]> =>
      this.get<UsageHistoryRow[]>(
        months != null
          ? `/api/usage/history?months=${months}`
          : "/api/usage/history"
      ),
  };

  readonly signals = {
    deliveries: (id: string): Promise<SignalDeliveryRow[]> =>
      this.get<SignalDeliveryRow[]>(`/api/signals/${id}/deliveries`),
  };

  // Legacy signal endpoints (pre-v2) kept for the list page and detail page.
  // The list page reads from /api/signals which returns Signal with overallStatus.
  // Individual signal fetch also uses the legacy /api/signals/:id endpoint.
  readonly legacySignals = {
    get: (id: string) =>
      this.get<import("./types").Signal>(`/api/signals/${id}`),
  };

  readonly onboarding = {
    status: (): Promise<{ data: OnboardingStatus }> =>
      this.get<{ data: OnboardingStatus }>("/api/onboarding/status"),
  };

  readonly support = {
    list: (params?: { status?: SupportTicketStatus; page?: number; size?: number }) =>
      this.get<SupportTicketListResponse>(qs("/api/support/tickets", params as Record<string, unknown>)),
    get: (id: string) =>
      this.get<SupportTicketDetail>(`/api/support/tickets/${id}`),
    create: (input: { subject: string; category: SupportTicketCategory; body: string }) =>
      this.post<{ id: string }>("/api/support/tickets", input),
    postMessage: (id: string, body: string) =>
      this.post<{ ok: boolean }>(`/api/support/tickets/${id}/messages`, { body }),
    close: (id: string) =>
      this.post<{ ok: boolean }>(`/api/support/tickets/${id}/close`),
  };

  readonly billing = {
    status: (): Promise<BillingStatus> =>
      this.get<BillingStatus>("/api/billing/status"),

    /**
     * Re-reads the subscription from Stripe, then returns the refreshed status.
     * For returning from the Stripe-hosted portal, where a cancel has happened
     * but the webhook that records it has not arrived yet.
     */
    sync: (): Promise<BillingStatus> =>
      this.post<BillingStatus>("/api/billing/sync", {}),

    checkout: (body: { planCode: string; interval: "monthly" | "annual" }): Promise<{ url: string }> =>
      this.post<{ url: string }>("/api/billing/checkout", body),

    portal: (): Promise<{ url: string }> =>
      this.post<{ url: string }>("/api/billing/portal", {}),

    cancel: (): Promise<{ url: string }> =>
      this.post<{ url: string }>("/api/billing/cancel", {}),

    changePlan: (body: { planCode: string; interval: "monthly" | "annual" }): Promise<{ immediate: boolean; scheduledFor: string | null }> =>
      this.post<{ immediate: boolean; scheduledFor: string | null }>("/api/billing/change-plan", body),
  };

  readonly admin = {
    users: {
      list: (filters?: Record<string, unknown>) =>
        this.get<{ items: AdminUserListItem[]; total: number; page: number; size: number }>(
          qs("/api/admin/users", filters),
        ),
      get: (id: string) => this.get<AdminUserDetail>(`/api/admin/users/${id}`),
      changePlan: (id: string, plan: string) =>
        this.put<{ message: string }>(`/api/admin/users/${id}/plan`, { plan }),
      extendTrial: (id: string, days: number) =>
        this.put<{ message: string }>(`/api/admin/users/${id}/trial`, { days }),
      disable: (id: string) =>
        this.post<{ message: string }>(`/api/admin/users/${id}/disable`),
      enable: (id: string) =>
        this.post<{ message: string }>(`/api/admin/users/${id}/enable`),
      resetPassword: (id: string) =>
        this.post<{ message: string }>(`/api/admin/users/${id}/reset-password`),
      verifyEmail: (id: string) =>
        this.post<{ message: string }>(`/api/admin/users/${id}/verify-email`),
      delete: (id: string) => this.del<{ message: string }>(`/api/admin/users/${id}`),
      usage: {
        current: (userId: string): Promise<UsageCurrent> =>
          this.get<UsageCurrent>(`/api/admin/users/${userId}/usage/current`),
        history: (userId: string, months: number = 24): Promise<UsageHistoryRow[]> =>
          this.get<UsageHistoryRow[]>(
            qs(`/api/admin/users/${userId}/usage/history`, { months }),
          ),
      },
    },

    sources: {
      list: (filters?: Record<string, unknown>) =>
        this.get<{ items: AdminSourceListItem[]; total: number; page: number; size: number }>(
          qs("/api/admin/sources", filters),
        ),
      get: (id: string) => this.get<AdminSourceDetail>(`/api/admin/sources/${id}`),
      update: (id: string, body: unknown) =>
        this.patch<{ message: string }>(`/api/admin/sources/${id}`, body),
      enable: (id: string) =>
        this.post<{ message: string }>(`/api/admin/sources/${id}/enable`),
      disable: (id: string) =>
        this.post<{ message: string }>(`/api/admin/sources/${id}/disable`),
      revealSecrets: (id: string) =>
        this.post<{ config: Record<string, string>; revealedUntil: string }>(
          `/api/admin/sources/${id}/reveal-secrets`,
        ),
      delete: (id: string, confirm = true) =>
        this.del<{ message: string }>(
          qs(`/api/admin/sources/${id}`, { confirm }),
        ),
    },

    targets: {
      list: (filters?: Record<string, unknown>) =>
        this.get<{ items: AdminTargetListItem[]; total: number; page: number; size: number }>(
          qs("/api/admin/targets", filters),
        ),
      get: (id: string) => this.get<AdminTargetDetail>(`/api/admin/targets/${id}`),
      update: (id: string, body: unknown) =>
        this.patch<{ message: string }>(`/api/admin/targets/${id}`, body),
      enable: (id: string) =>
        this.post<{ message: string }>(`/api/admin/targets/${id}/enable`),
      disable: (id: string) =>
        this.post<{ message: string }>(`/api/admin/targets/${id}/disable`),
      revealSecrets: (id: string) =>
        this.post<{ config: Record<string, string | null>; revealedUntil: string }>(
          `/api/admin/targets/${id}/reveal-secrets`,
        ),
      forceDisconnect: (id: string) =>
        this.post<{ message: string }>(`/api/admin/targets/${id}/force-disconnect`),
      delete: (id: string, confirm = true) =>
        this.del<{ message: string }>(
          qs(`/api/admin/targets/${id}`, { confirm }),
        ),
    },

    routes: {
      list: (filters?: Record<string, unknown>) =>
        this.get<{ items: AdminRouteListItem[]; total: number; page: number; size: number }>(
          qs("/api/admin/routes", filters),
        ),
      get: (id: string) => this.get<AdminRouteDetail>(`/api/admin/routes/${id}`),
      update: (id: string, body: unknown) =>
        this.patch<{ message: string }>(`/api/admin/routes/${id}`, body),
      updateSteps: (id: string, steps: unknown[]) =>
        this.put<{ message: string }>(`/api/admin/routes/${id}/steps`, steps),
      enable: (id: string) =>
        this.post<{ message: string }>(`/api/admin/routes/${id}/enable`),
      disable: (id: string) =>
        this.post<{ message: string }>(`/api/admin/routes/${id}/disable`),
      delete: (id: string, confirm = true) =>
        this.del<{ message: string }>(
          qs(`/api/admin/routes/${id}`, { confirm }),
        ),
    },

    signals: {
      list: (filters?: Record<string, unknown>) =>
        this.get<{ items: AdminSignalListItem[]; total: number; page: number; size: number }>(
          qs("/api/admin/signals", filters),
        ),
      trace: (id: string) => this.get<AdminSignalTrace>(`/api/admin/signals/${id}`),
    },

    deliveries: {
      list: (filters?: Record<string, unknown>) =>
        this.get<{ items: AdminDeliveryListItem[]; total: number; page: number; size: number }>(
          qs("/api/admin/deliveries", filters),
        ),
      get: (id: string) => this.get<AdminDeliveryDetail>(`/api/admin/deliveries/${id}`),
      retry: (id: string) =>
        this.post<{ newDeliveryId: string; status: string }>(
          `/api/admin/deliveries/${id}/retry`,
        ),
    },

    audit: {
      list: (filters?: Record<string, unknown>) =>
        this.get<{ items: AdminAuditListItem[]; total: number; page: number; size: number }>(
          qs("/api/admin/audit", filters),
        ),
      get: (id: string) => this.get<AdminAuditEntryDetail>(`/api/admin/audit/${id}`),
    },

    billing: {
      stats: () => this.get<AdminBillingStats>("/api/admin/billing/stats"),
      trials: (filters?: Record<string, unknown>) =>
        this.get<{ items: AdminTrialItem[]; total: number; page: number; size: number }>(
          qs("/api/admin/billing/trials", filters),
        ),
    },

    logs: {
      errors: (filters?: Record<string, unknown>) =>
        this.get<{ items: AdminErrorLogItem[]; total: number; page: number; size: number }>(
          qs("/api/admin/logs/errors", filters),
        ),
    },

    overview: {
      stats: () => this.get<AdminOverviewResponse>("/api/admin/overview/stats"),
      needsAttention: () =>
        this.get<{ items: AdminNeedsAttentionItem[] }>("/api/admin/overview/needs-attention"),
      activity: (cursor?: string | null, limit: number = 50) =>
        this.get<{ items: AdminActivityEvent[]; nextCursor: string | null }>(
          qs("/api/admin/overview/activity", { cursor: cursor ?? undefined, limit }),
        ),
    },

    settings: {
      get: () => this.get<AdminSettingsResponse>("/api/admin/settings"),
      updateSystemConfig: (key: string, value: string) =>
        this.put<void>(
          `/api/admin/settings/system-config/${encodeURIComponent(key)}`,
          { value },
        ),
    },

    support: {
      list: (filter?: AdminSupportFilter): Promise<AdminSupportListResponse> => {
        const params: Record<string, unknown> = {};
        if (filter?.statuses?.length) params.statuses = filter.statuses.join(",");
        if (filter?.priorities?.length) params.priorities = filter.priorities.join(",");
        if (filter?.categories?.length) params.categories = filter.categories.join(",");
        if (filter?.assignee) params.assignee = filter.assignee;
        if (filter?.userId) params.userId = filter.userId;
        if (filter?.search) params.search = filter.search;
        if (filter?.dateFrom) params.dateFrom = filter.dateFrom;
        if (filter?.dateTo) params.dateTo = filter.dateTo;
        if (filter?.page) params.page = filter.page;
        if (filter?.size) params.size = filter.size;
        return this.get<AdminSupportListResponse>(qs("/api/admin/support/tickets", params));
      },
      get: (id: string): Promise<AdminSupportTicketDetail> =>
        this.get<AdminSupportTicketDetail>(`/api/admin/support/tickets/${id}`),
      postMessage: (id: string, body: string): Promise<{ ok: boolean }> =>
        this.post<{ ok: boolean }>(`/api/admin/support/tickets/${id}/messages`, { body }),
      triage: (id: string, patch: { priority?: SupportTicketPriority; category?: SupportTicketCategory; assigneeAdminId?: string | null }): Promise<{ ok: boolean }> =>
        this.patch<{ ok: boolean }>(`/api/admin/support/tickets/${id}`, patch),
      close: (id: string): Promise<{ ok: boolean }> =>
        this.post<{ ok: boolean }>(`/api/admin/support/tickets/${id}/close`),
      reopen: (id: string): Promise<{ ok: boolean }> =>
        this.post<{ ok: boolean }>(`/api/admin/support/tickets/${id}/reopen`),
    },

    plans: {
      list: (): Promise<{ data: AdminPlan[] }> =>
        this.get<{ data: AdminPlan[] }>("/api/admin/plans"),
      get: (planCode: string): Promise<{ data: AdminPlan }> =>
        this.get<{ data: AdminPlan }>(`/api/admin/plans/${planCode}`),
      update: (planCode: string, body: UpdatePlanRequest): Promise<{ data: AdminPlan }> =>
        this.put<{ data: AdminPlan }>(`/api/admin/plans/${planCode}`, body),
      publish: (planCode: string): Promise<{ data: AdminPlan }> =>
        this.post<{ data: AdminPlan }>(`/api/admin/plans/${planCode}/publish`),
    },
  };

  readonly plans = {
    list: (): Promise<{ data: PublicPlan[] }> =>
      this.get<{ data: PublicPlan[] }>("/api/plans"),
  };

  readonly user = {
    updateLocale: async (locale: string): Promise<void> => {
      const res = await this.patch<{ accessToken: string }>("/api/user/locale", { locale });
      // Replace the stored access token so subsequent API calls include the
      // updated 'locale' claim. Refresh token is unchanged.
      const refresh = getRefreshToken();
      if (res.accessToken && refresh) setTokens(res.accessToken, refresh);
    },
  };
}

export const api = new ApiClient();
export { ApiError };
